import { useState, useEffect, useRef } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { MessageCircle, Send, X, Loader2, CheckCircle, User, ThumbsUp, ThumbsDown, Paperclip, FileText } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Session expiry: 30 days
const SESSION_EXPIRY_DAYS = 30;

// ── Phase B: rich chat rendering (tables + YouTube/Vimeo embeds) ──
// Styled renderers so GFM markdown tables show as real tables inside the bubble.
const chatMarkdownComponents = {
  table: ({ node, ...props }) => (
    <div className="overflow-x-auto my-2" data-testid="chat-md-table">
      <table className="min-w-full text-xs border-collapse border border-slate-300" {...props} />
    </div>
  ),
  thead: ({ node, ...props }) => <thead className="bg-slate-100" {...props} />,
  th: ({ node, ...props }) => <th className="border border-slate-300 px-2 py-1 text-left font-semibold" {...props} />,
  td: ({ node, ...props }) => <td className="border border-slate-300 px-2 py-1 align-top" {...props} />,
  a: ({ node, ...props }) => <a className="text-blue-600 underline" target="_blank" rel="noopener noreferrer" {...props} />,
};

// Only YouTube/Vimeo IDs are extracted; the iframe src is rebuilt from trusted
// domains (never the raw user URL), so embeds are domain-locked by construction.
const _VIDEO_MATCHERS = [
  { type: "youtube", re: /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/g, embed: (id) => `https://www.youtube.com/embed/${id}` },
  { type: "vimeo", re: /(?:https?:\/\/)?(?:www\.)?vimeo\.com\/(?:video\/)?(\d+)/g, embed: (id) => `https://player.vimeo.com/video/${id}` },
];

function extractVideoEmbeds(text) {
  const found = [];
  const seen = new Set();
  if (!text) return found;
  for (const m of _VIDEO_MATCHERS) {
    const re = new RegExp(m.re);
    let match;
    while ((match = re.exec(text)) !== null) {
      const id = match[1];
      const key = m.type + ":" + id;
      if (!seen.has(key)) {
        seen.add(key);
        found.push({ type: m.type, src: m.embed(id) });
      }
    }
  }
  return found;
}

const ChatMarkdown = ({ content }) => {
  const videos = extractVideoEmbeds(content);
  return (
    <>
      <div className="text-sm prose prose-sm max-w-none break-words">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={chatMarkdownComponents}>
          {content}
        </ReactMarkdown>
      </div>
      {videos.map((v, i) => (
        <div
          key={i}
          className="mt-2 rounded-lg overflow-hidden border border-slate-200"
          data-testid="chat-video-embed"
        >
          <iframe
            src={v.src}
            title={`${v.type}-video-${i}`}
            className="w-full"
            style={{ aspectRatio: "16 / 9", border: 0 }}
            loading="lazy"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      ))}
    </>
  );
};

export default function ChatWindow({ actorId, actorName, actorImage, userSessionEnabled = false, welcomeMessages = null, actorLanguage = "en", uiLabels = {}, fileAttachmentsEnabled = false, allowedFileTypes = [], trainingMode = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState(trainingMode ? "trainer_identify" : (userSessionEnabled ? "initial" : "loading"));

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [isTrainer, setIsTrainer] = useState(false);
  const [ratings, setRatings] = useState({}); // { [message_id]: {open, verdict, errorType, why, corrected, submitted} }
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [attachmentId, setAttachmentId] = useState(null);
  const fileInputRef = useRef(null);
  const [remainingMessages, setRemainingMessages] = useState(null);
  const [rateLimitingEnabled, setRateLimitingEnabled] = useState(true);
  const [feedbackGiven, setFeedbackGiven] = useState({});
  const [chatLocked, setChatLocked] = useState(false);
  const [redirectInfo, setRedirectInfo] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Simple welcome message getter — uses props from parent (resolved at page load)
  const getWelcomeMessage = (type, visitorName = "") => {
    if (!welcomeMessages || !welcomeMessages[type]) {
      return `Hi! I'm ${actorName}. How can I help you today?`;
    }
    return welcomeMessages[type].replace(/\{visitor_name\}/g, visitorName || "Guest");
  };

  // Check for existing session on mount — runs immediately (no async config dependency)
  useEffect(() => {
    // Wait for welcome messages to be passed from parent
    if (!welcomeMessages) return;

    // Training mode: always start with trainer identification (no session restore)
    if (trainingMode) {
      setStep("trainer_identify");
      return;
    }

    const storageKey = `chat_session_${actorId}`;
    const savedSession = localStorage.getItem(storageKey);

    if (savedSession) {
      try {
        const parsed = JSON.parse(savedSession);
        const savedId = parsed.sessionId;
        const savedName = parsed.name;
        const savedEmail = parsed.email;
        const savedExpiry = parsed.expiryDate;
        const now = new Date().getTime();
        const expiry = new Date(savedExpiry).getTime();

        if (now < expiry && savedId) {
          axios.get(`${API_URL}/api/public/chat/session-check/${savedId}`)
            .then(() => {
              setSessionId(savedId);
              setName(savedName);
              setEmail(savedEmail);
              setMessages([{
                role: "assistant",
                content: getWelcomeMessage("returning_visitor", savedName),
                timestamp: new Date().toISOString()
              }]);
              setStep("chat");
            })
            .catch(() => {
              localStorage.removeItem(storageKey);
              startFreshGuest();
            });
          return;
        } else {
          localStorage.removeItem(storageKey);
        }
      } catch (e) {
        localStorage.removeItem(storageKey);
      }
    }

    startFreshGuest();
  }, [actorId, welcomeMessages]);

  const startFreshGuest = () => {
    if (userSessionEnabled) {
      setStep("initial");
      return;
    }
    const guestId = `guest_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
    const pseudoSessionId = `pseudo_${guestId}`;
    setSessionId(pseudoSessionId);
    setName("Guest");
    setEmail("");
    setStep("chat");
    setMessages([{
      role: "assistant",
      content: getWelcomeMessage("new_visitor"),
      timestamp: new Date().toISOString()
    }]);
    const storageKey = `chat_session_${actorId}`;
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + 30);
    localStorage.setItem(storageKey, JSON.stringify({
      sessionId: pseudoSessionId,
      name: "Guest",
      email: "",
      expiryDate: expiryDate.toISOString()
    }));
  };

  // Auto-focus input when chat is ready and after sending message
  useEffect(() => {
    if (step === "chat" && !sending && inputRef.current) {
      inputRef.current.focus();
    }
  }, [step, sending, messages]);

  const requestOTP = async () => {
    if (!name || !email) {
      toast.error("Please enter your name and email");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/public/chat/request-otp`, {
        email,
        name,
        actor_id: actorId
      });
      toast.success("OTP sent to your email!");
      setStep("otp");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = async () => {
    if (!otp || otp.length !== 6) {
      toast.error("Please enter the 6-digit OTP");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/public/chat/verify-otp`, {
        email,
        otp,
        actor_id: actorId
      });
      
      const newSessionId = response.data.session_id;
      setSessionId(newSessionId);
      
      // Save session to localStorage with 30-day expiry
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + SESSION_EXPIRY_DAYS);
      
      const storageKey = `chat_session_${actorId}`;
      localStorage.setItem(storageKey, JSON.stringify({
        sessionId: newSessionId,
        name: response.data.name,
        email: email,
        expiryDate: expiryDate.toISOString()
      }));
      
      setStep("chat");
      toast.success("Verified! You can now chat.");
      
      // Add welcome message
      setMessages([
        {
          role: "assistant",
          content: getWelcomeMessage("post_otp", response.data.name),
          timestamp: new Date().toISOString()
        }
      ]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  // Handle file selection
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (allowedFileTypes.length > 0 && !allowedFileTypes.includes(ext)) {
      toast.error(`File type .${ext} not allowed`);
      return;
    }
    setSelectedFile(file);
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('actor_id', actorId);
      formData.append('session_id', sessionId);
      const res = await axios.post(`${API}/public/chat/upload-attachment`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setAttachmentId(res.data.attachment_id);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Upload failed';
      toast.error(detail);
      setSelectedFile(null);
      setAttachmentId(null);
    }
    setUploadingFile(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const clearAttachment = () => {
    setSelectedFile(null);
    setAttachmentId(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const sendMessage = async () => {
    if (!newMessage.trim() && !attachmentId) return;
    // Guard: never send while a file is still uploading, otherwise the message
    // would be sent with the PREVIOUS upload's id (off-by-one attachment bug).
    if (uploadingFile || (selectedFile && !attachmentId)) {
      toast.error("Please wait for the file to finish uploading");
      return;
    }

    const userMessage = newMessage.trim();
    setNewMessage("");
    setSending(true);

    // Add user message to UI immediately (with attachment info)
    setMessages(prev => [...prev, {
      role: "user",
      content: userMessage || "(file attached)",
      attachment: selectedFile ? { filename: selectedFile.name, file_type: selectedFile.name.split('.').pop()?.toLowerCase() } : null,
      timestamp: new Date().toISOString()
    }]);

    const currentAttachmentId = attachmentId;
    clearAttachment();

    let isConnectionLost = false;

    // Get user's timezone for appointment booking
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    try {
      const response = await axios.post(`${API}/public/chat/message`, {
        session_id: sessionId,
        message: `${userMessage}|TZ:${userTimezone}`,
        actor_id: actorId,
        browser_language: navigator.language || navigator.userLanguage || "en",
        attachment_id: currentAttachmentId || null,
      });

      // Handle chat_locked response (registered_only actor blocked further messages)
      if (response.data.chat_locked && response.data.redirect) {
        const rd = response.data.redirect;
        setChatLocked(true);
        setRedirectInfo(rd);
        // Remove the last user message from display (it was blocked)
        setMessages(prev => prev.filter((m, i) => !(i === prev.length - 1 && m.role === "user")));
        setSending(false);
        return;
      }

      // Add assistant response with message ID for feedback
      if (response.data.message) {
        setMessages(prev => [...prev, {
          role: "assistant",
          content: response.data.message,
          message_id: response.data.message_id,
          timestamp: new Date().toISOString()
        }]);
      }

      // Handle redirect response (first time — lock the chat)
      if (response.data.redirect) {
        const rd = response.data.redirect;
        setChatLocked(true);
        setRedirectInfo(rd);
      }

      // Update rate limiting state
      if (response.data.rate_limiting_enabled !== undefined) {
        setRateLimitingEnabled(response.data.rate_limiting_enabled);
      }
      // Only update remaining messages if rate limiting is enabled
      if (response.data.remaining_messages !== null && response.data.remaining_messages !== undefined) {
        setRemainingMessages(response.data.remaining_messages);
      } else {
        setRemainingMessages(null); // Unlimited
      }
    } catch (error) {
      const errorDetail = error.response?.data?.detail || "Failed to send message";
      
      // Check if it's a connection lost error
      if (errorDetail === "CONNECTION_LOST" || error.response?.status === 503) {
        isConnectionLost = true;
        toast.error("Connection lost. Please try again later.");
        // Disable chat
        setMessages(prev => [...prev, {
          role: "system",
          content: "⚠️ Connection lost. The chat has been disconnected. Please refresh the page to start a new conversation.",
          timestamp: new Date().toISOString()
        }]);
        // Keep it disabled by NOT setting setSending(false)
      } else {
        toast.error(errorDetail);
        // Remove the optimistic user message on error
        setMessages(prev => prev.slice(0, -1));
        setNewMessage(userMessage); // Restore the message
      }
    } finally {
      // Only re-enable sending if it wasn't a connection lost error
      if (!isConnectionLost) {
        setSending(false);
      }
      
      // Keep focus on input after sending
      if (inputRef.current && !isConnectionLost) {
        setTimeout(() => {
          inputRef.current?.focus();
        }, 100);
      }
    }
  };

  const submitFeedback = async (messageId, feedbackType) => {
    try {
      await axios.post(`${API}/public/chat/feedback`, {
        session_id: sessionId,
        actor_id: actorId,
        message_id: messageId,
        feedback_type: feedbackType,
        feedback_comment: null
      });
      
      setFeedbackGiven(prev => ({ ...prev, [messageId]: feedbackType }));
      toast.success(`Thank you for your feedback!`);
    } catch (error) {
      toast.error("Failed to submit feedback");
      console.error(error);
    }
  };

  const identifyTrainer = async () => {
    if (!name || !email) {
      toast.error("Please enter your name and email");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/public/training/identify`, {
        actor_id: actorId, name, email,
      });
      setSessionId(res.data.session_id);
      setIsTrainer(!!res.data.is_trainer);
      setStep("chat");
      setMessages([{
        role: "assistant",
        content: res.data.is_trainer
          ? `Hi ${res.data.name}! You're verified as a trainer. Chat normally — rate each of my replies with 👍/👎 and a reason.`
          : getWelcomeMessage("new_visitor"),
        timestamp: new Date().toISOString(),
      }]);
      if (res.data.is_trainer) toast.success("Verified as trainer");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to identify");
    } finally {
      setLoading(false);
    }
  };

  const updateRating = (id, patch) => {
    setRatings((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), ...patch } }));
  };

  const submitRating = async (messageId) => {
    const r = ratings[messageId] || {};
    if (!r.verdict) return toast.error("Pick 👍 or 👎 first");
    try {
      updateRating(messageId, { submitting: true });
      await axios.post(`${API}/public/training/rate`, {
        session_id: sessionId,
        actor_id: actorId,
        message_id: messageId,
        verdict: r.verdict,
        error_type: r.verdict === "good" ? null : (r.errorType || "fact"),
        why_text: r.why || null,
        corrected_answer: r.corrected || null,
      });
      updateRating(messageId, { submitted: true, open: false, submitting: false });
      toast.success("Rating saved — admin will review it");
    } catch (error) {
      updateRating(messageId, { submitting: false });
      toast.error(error.response?.data?.detail || "Failed to save rating");
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (step === "trainer_identify") {
        identifyTrainer();
      } else if (step === "initial") {
        requestOTP();
      } else if (step === "otp") {
        verifyOTP();
      } else {
        sendMessage();
      }
    }
  };

  return (
    <>
      {/* Floating Chat Button with Actor Image */}
      {!isOpen && (
        <div className="fixed bottom-24 right-6 z-[9998]">
          <button
            onClick={() => setIsOpen(true)}
            className="relative group"
          >
            {/* Actor Profile Image */}
            <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-white shadow-2xl hover:scale-110 transition-transform duration-200 bg-slate-900">
              {actorImage ? (
                <img
                  src={`${API}/ai/actors/images/${actorImage}`}
                  alt={actorName}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-slate-900">
                  <User className="w-10 h-10 text-white" />
                </div>
              )}
            </div>
            
            {/* Pulsing indicator */}
            <div className="absolute -top-1 -right-1">
              <div className="relative">
                <div className="w-5 h-5 bg-emerald-500 rounded-full border-2 border-white"></div>
                <div className="absolute inset-0 w-5 h-5 bg-emerald-500 rounded-full animate-ping"></div>
              </div>
            </div>
            
            {/* Chat message icon */}
            <div className="absolute -bottom-2 -right-2 bg-slate-900 rounded-full p-2 shadow-lg">
              <MessageCircle className="w-5 h-5 text-white" />
            </div>
            
            {/* Tooltip */}
            <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-slate-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
              {uiLabels.chat_with || "Chat with"} {actorName}
              <div className="absolute top-full right-6 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-slate-900"></div>
            </div>
          </button>
        </div>
      )}

      {/* Chat Window - Higher z-index to be above "Made with Emergent" badge */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 z-[10000] w-[calc(100vw-3rem)] h-[calc(100vh-8rem)] md:bottom-0 md:right-0 md:top-0 md:w-[50vw] md:h-screen max-w-full">
          <Card className="shadow-2xl border-2 h-full flex flex-col">
            <CardHeader className="bg-slate-900 text-white rounded-t-lg md:rounded-none flex-shrink-0">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">{uiLabels.chat_with || "Chat with"} {actorName}</CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsOpen(false)}
                  className="text-white hover:bg-slate-800"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>

            <CardContent className="p-0 flex-1 flex flex-col overflow-hidden">
              {step === "loading" && (
                <div className="flex items-center justify-center flex-1 p-6">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              )}

              {step === "trainer_identify" && (
                <div className="p-6 space-y-4" data-testid="trainer-identify-step">
                  <div className="flex items-center gap-2 text-indigo-600">
                    <CheckCircle className="w-5 h-5" />
                    <span className="text-sm font-medium">Training Mode active</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Please tell us who you are to begin. Verified trainers can rate each reply.
                  </p>
                  <div>
                    <Label>Name</Label>
                    <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" onKeyPress={handleKeyPress} data-testid="trainer-name-input" />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" onKeyPress={handleKeyPress} data-testid="trainer-email-input" />
                  </div>
                  <Button onClick={identifyTrainer} disabled={loading} className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="trainer-continue-btn">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Continue"}
                  </Button>
                </div>
              )}

              {step === "initial" && (
                <div className="p-6 space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Enter your details to start chatting. We'll send you a verification code.
                  </p>
                  <div>
                    <Label>Name</Label>
                    <Input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Your name"
                      onKeyPress={handleKeyPress}
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      onKeyPress={handleKeyPress}
                    />
                  </div>
                  <Button
                    onClick={requestOTP}
                    disabled={loading}
                    className="w-full bg-slate-900 hover:bg-slate-800"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send Verification Code"}
                  </Button>
                </div>
              )}

              {step === "otp" && (
                <div className="p-6 space-y-4">
                  <div className="flex items-center gap-2 text-emerald-600 mb-2">
                    <CheckCircle className="w-5 h-5" />
                    <span className="text-sm font-medium">Code sent to {email}</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Enter the 6-digit code we sent to your email.
                  </p>
                  <div>
                    <Label>Verification Code</Label>
                    <Input
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      maxLength={6}
                      className="text-center text-2xl tracking-widest"
                      onKeyPress={handleKeyPress}
                    />
                  </div>
                  <Button
                    onClick={verifyOTP}
                    disabled={loading}
                    className="w-full bg-slate-900 hover:bg-slate-800"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify & Start Chat"}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => setStep("initial")}
                    className="w-full text-sm"
                  >
                    Change email
                  </Button>
                </div>
              )}

              {step === "chat" && (
                <>
                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50">
                    {messages.map((msg, index) => (
                      <div
                        key={index}
                        className={`flex flex-col ${msg.role === "user" ? "items-end" : "items-start"}`}
                      >
                        <div
                          className={`max-w-[80%] rounded-lg px-4 py-2 ${
                            msg.role === "user"
                              ? "bg-slate-900 text-white"
                              : "bg-white border border-slate-200"
                          }`}
                        >
                          {/* Attachment badge */}
                          {msg.attachment && (
                            <div className="flex items-center gap-1.5 mb-1 text-xs opacity-75">
                              <FileText className="w-3 h-3" />
                              <span className="truncate">{msg.attachment.filename}</span>
                            </div>
                          )}
                          <ChatMarkdown content={msg.content} />
                        </div>
                        {/* Feedback / Trainer rating for Assistant Messages */}
                        {msg.role === "assistant" && msg.message_id && !isTrainer && (
                          <div className="flex items-center gap-2 mt-2 ml-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => submitFeedback(msg.message_id, "positive")}
                              disabled={feedbackGiven[msg.message_id]}
                              className={`h-7 px-2 ${
                                feedbackGiven[msg.message_id] === "positive"
                                  ? "text-green-600 bg-green-50"
                                  : "text-slate-400 hover:text-green-600 hover:bg-green-50"
                              }`}
                            >
                              <ThumbsUp className="w-3 h-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => submitFeedback(msg.message_id, "negative")}
                              disabled={feedbackGiven[msg.message_id]}
                              className={`h-7 px-2 ${
                                feedbackGiven[msg.message_id] === "negative"
                                  ? "text-red-600 bg-red-50"
                                  : "text-slate-400 hover:text-red-600 hover:bg-red-50"
                              }`}
                            >
                              <ThumbsDown className="w-3 h-3" />
                            </Button>
                            {feedbackGiven[msg.message_id] && (
                              <span className="text-xs text-muted-foreground">Thank you!</span>
                            )}
                          </div>
                        )}

                        {/* Trainer rating widget (fact/behavioral) */}
                        {msg.role === "assistant" && msg.message_id && isTrainer && (
                          <TrainerRating
                            msgId={msg.message_id}
                            r={ratings[msg.message_id] || {}}
                            updateRating={updateRating}
                            submitRating={submitRating}
                          />
                        )}
                      </div>
                    ))}
                    {sending && (
                      <div className="flex justify-start">
                        <div className="bg-white border border-slate-200 rounded-lg px-4 py-3">
                          <div className="flex items-center gap-1">
                            <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                            <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                            <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                          </div>
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Input or Redirect Banner */}
                  {chatLocked && redirectInfo ? (
                    <div className="p-4 border-t flex-shrink-0 bg-amber-50 border-amber-200">
                      <p className="text-sm text-amber-800 font-medium mb-3">
                        {redirectInfo.labels?.not_registered || "You are not a registered client."}
                      </p>
                      <div className="flex flex-col gap-2">
                        {/* Redirect actor card */}
                        {(() => {
                          const isIframe = window.self !== window.top;
                          const labels = redirectInfo.labels || {};
                          return (
                            <div className="p-3 bg-white rounded-lg border border-indigo-200">
                              <div className="flex items-center gap-3 mb-2">
                                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-sm">
                                  {(redirectInfo.actor_name || "?")[0]}
                                </div>
                                <div>
                                  <p className="text-sm font-semibold text-gray-900">{redirectInfo.actor_name}</p>
                                  {redirectInfo.designation && (
                                    <p className="text-xs text-gray-500">{redirectInfo.designation}</p>
                                  )}
                                </div>
                              </div>
                              {isIframe ? (
                                <button
                                  onClick={() => {
                                    if (redirectInfo.public_url) {
                                      window.location.href = redirectInfo.public_url;
                                    }
                                  }}
                                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 transition-colors font-medium"
                                >
                                  {labels.switch_chat || "Switch Chat"} →
                                </button>
                              ) : (
                                <a
                                  href={redirectInfo.public_url || "#"}
                                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 transition-colors font-medium no-underline"
                                >
                                  {labels.open_chat || "Open Chat"} →
                                </a>
                              )}
                            </div>
                          );
                        })()}
                        <button
                          onClick={() => setIsOpen(false)}
                          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-300 transition-colors"
                        >
                          {redirectInfo.labels?.close_chat || "Close Chat"}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 border-t flex-shrink-0">
                      {/* File attachment preview */}
                      {selectedFile && (
                        <div className="flex items-center gap-2 mb-2 px-2 py-1.5 bg-slate-50 rounded-lg text-xs">
                          <FileText className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                          <span className="truncate flex-1">{selectedFile.name}</span>
                          <span className="text-slate-400 shrink-0">{(selectedFile.size / 1024).toFixed(0)} KB</span>
                          {uploadingFile ? (
                            <Loader2 className="w-3 h-3 animate-spin text-slate-400 shrink-0" />
                          ) : (
                            <button onClick={clearAttachment} className="text-slate-400 hover:text-red-500 shrink-0"><X className="w-3.5 h-3.5" /></button>
                          )}
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        {/* File attachment button */}
                        {fileAttachmentsEnabled && (
                          <>
                            <input
                              ref={fileInputRef}
                              type="file"
                              className="hidden"
                              accept={allowedFileTypes.map(t => `.${t}`).join(',')}
                              onChange={handleFileSelect}
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-9 w-9 p-0 shrink-0"
                              onClick={() => fileInputRef.current?.click()}
                              disabled={sending || uploadingFile}
                              title="Attach file"
                              data-testid="attach-file-btn"
                            >
                              <Paperclip className="w-4 h-4 text-slate-500" />
                            </Button>
                          </>
                        )}
                        <Input
                          ref={inputRef}
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          placeholder={uiLabels.type_message || "Type your message..."}
                          onKeyPress={handleKeyPress}
                          disabled={sending || (rateLimitingEnabled && remainingMessages !== null && remainingMessages <= 0)}
                          autoFocus
                        />
                        <Button
                          onClick={sendMessage}
                          disabled={sending || uploadingFile || (selectedFile && !attachmentId) || (!newMessage.trim() && !attachmentId) || (rateLimitingEnabled && remainingMessages !== null && remainingMessages <= 0)}
                          className="bg-slate-900 hover:bg-slate-800"
                        >
                          <Send className="w-4 h-4" />
                        </Button>
                      </div>
                      {rateLimitingEnabled && remainingMessages !== null ? (
                        <p className="text-xs text-muted-foreground mt-2">
                          {remainingMessages} messages remaining today
                        </p>
                      ) : null}
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
}

function TrainerRating({ msgId, r, updateRating, submitRating }) {
  if (r.submitted) {
    return (
      <div className="mt-2 ml-2 text-xs text-emerald-600 flex items-center gap-1" data-testid={`trainer-rated-${msgId}`}>
        <CheckCircle className="w-3 h-3" /> Rated {r.verdict === "good" ? "👍" : "👎"}{r.errorType && r.verdict === "bad" ? ` · ${r.errorType}` : ""} — sent for review
      </div>
    );
  }
  return (
    <div className="mt-2 ml-2 w-full" data-testid={`trainer-rating-${msgId}`}>
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm"
          onClick={() => updateRating(msgId, { verdict: "good", open: true })}
          className={`h-7 px-2 ${r.verdict === "good" ? "text-green-600 bg-green-50" : "text-slate-400 hover:text-green-600 hover:bg-green-50"}`}
          data-testid={`trainer-up-${msgId}`}>
          <ThumbsUp className="w-3 h-3" />
        </Button>
        <Button variant="ghost" size="sm"
          onClick={() => updateRating(msgId, { verdict: "bad", open: true, errorType: r.errorType || "fact" })}
          className={`h-7 px-2 ${r.verdict === "bad" ? "text-red-600 bg-red-50" : "text-slate-400 hover:text-red-600 hover:bg-red-50"}`}
          data-testid={`trainer-down-${msgId}`}>
          <ThumbsDown className="w-3 h-3" />
        </Button>
        <button onClick={() => updateRating(msgId, { open: !r.open })} className="text-[11px] font-medium text-indigo-600 hover:text-indigo-800" data-testid={`trainer-why-toggle-${msgId}`}>
          {r.open ? "Hide" : "Why?"}
        </button>
      </div>
      {r.open && (
        <div className="mt-2 p-2 rounded-md border border-indigo-200 bg-indigo-50/50 space-y-2">
          <div>
            <label className="text-[11px] font-medium text-slate-600">Reason</label>
            <textarea rows={2} value={r.why || ""} onChange={(e) => updateRating(msgId, { why: e.target.value })}
              placeholder="Why is this good / bad?"
              className="w-full text-xs border border-slate-200 rounded px-2 py-1 mt-0.5"
              data-testid={`trainer-why-input-${msgId}`} />
          </div>
          {r.verdict === "bad" && (
            <div>
              <label className="text-[11px] font-medium text-slate-600">Error type</label>
              <select value={r.errorType || "fact"} onChange={(e) => updateRating(msgId, { errorType: e.target.value })}
                className="w-full text-xs border border-slate-200 rounded px-2 py-1 mt-0.5 bg-white"
                data-testid={`trainer-error-type-${msgId}`}>
                <option value="fact">Fact error</option>
                <option value="behavioral">Behavioral error</option>
                <option value="other">Other</option>
              </select>
            </div>
          )}
          <div>
            <label className="text-[11px] font-medium text-slate-600">Correct answer (optional)</label>
            <textarea rows={2} value={r.corrected || ""} onChange={(e) => updateRating(msgId, { corrected: e.target.value })}
              placeholder="What should it have said?"
              className="w-full text-xs border border-slate-200 rounded px-2 py-1 mt-0.5"
              data-testid={`trainer-correct-input-${msgId}`} />
          </div>
          <Button size="sm" onClick={() => submitRating(msgId)} disabled={r.submitting || !r.verdict}
            className="h-7 text-xs bg-indigo-600 hover:bg-indigo-700" data-testid={`trainer-submit-${msgId}`}>
            {r.submitting ? "Saving…" : "Submit rating"}
          </Button>
        </div>
      )}
    </div>
  );
}
